import {Component, inject, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatButtonModule} from "@angular/material/button"
import {MatDialog} from "@angular/material/dialog"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatTableModule} from "@angular/material/table"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterOutlet} from "@angular/router"
import {TypedMatCellDefDirective} from "@app/common/directives"
import {OrganizationGroupsTableItemFragment} from "@app/platform/components/organization-groups/organization-groups-table/organization-groups-table.generated"
import {TableCellComponent} from "@common/components/tables/table-cell/table-cell.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {BreakpointsService} from "@common/services/breakpoints/breakpoints.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {UtilsService} from "@legacy/helpers/utils"
import {BaseTableComponent} from "@platform/components/base/base-table/base-table.component"
import {EditOrganizationGroupDialogComponent} from "@platform/components/organization-groups/edit-organization-group-dialog/edit-organization-group-dialog.component"
import {
    CreateOrganizationGroupsTableItemGQL,
    DeleteOrganizationGroupsTableItemGQL,
    OrganizationGroupsTableItemsGQL,
    UpdateOrganizationGroupsTableItemGQL,
} from "@platform/components/organization-groups/organization-groups-table/organization-groups-table.generated"
import {map, Subject, startWith} from "rxjs"

export type OrganizationGroupDialogResult = {
    id?: string | null
    name: string
    organizationIds: string[]
    create?: boolean
    delete?: boolean
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-organization-groups-table",
    templateUrl: "./organization-groups-table.component.html",
    styleUrls: ["./organization-groups-table.component.scss"],
    imports: [RouterOutlet, MatButtonModule, MatProgressBarModule, MatTableModule, MatTooltipModule, TableCellComponent, TypedMatCellDefDirective],
})
export class OrganizationGroupsTableComponent extends BaseTableComponent<OrganizationGroupsTableItemFragment> {
    readonly breakpoints = inject(BreakpointsService)
    readonly can = inject(PermissionsService)
    readonly dialog = inject(MatDialog)
    readonly utils = inject(UtilsService)

    readonly organizationGroupsTableItems = inject(OrganizationGroupsTableItemsGQL)
    readonly createOrganizationGroupsTableItem = inject(CreateOrganizationGroupsTableItemGQL)
    readonly deleteOrganizationGroupsTableItem = inject(DeleteOrganizationGroupsTableItemGQL)
    readonly updateOrganizationGroupsTableItem = inject(UpdateOrganizationGroupsTableItemGQL)

    readonly $columns = signal(["id", "name", "organizations", "actions"])
    private readonly reload$ = new Subject<void>()
    $data = this.dataLoader.$batchedData<OrganizationGroupsTableItemFragment>(
        this.reload$.pipe(
            startWith(undefined),
            map(() => (skip: number, take: number) => {
                return fetchThrowingErrors(this.organizationGroupsTableItems)({skip, take})
            }),
        ),
        this.infiniteScroll.shouldLoadMore$,
    )

    showEditDialog(item: OrganizationGroupsTableItemFragment | null = null): void {
        const dialogRef = this.dialog.open<EditOrganizationGroupDialogComponent, OrganizationGroupsTableItemFragment | null, OrganizationGroupDialogResult>(
            EditOrganizationGroupDialogComponent,
            {
                width: "350px",
                data: item,
            },
        )
        dialogRef.afterClosed().subscribe(async (result) => {
            // Navigate back to the list URL (away from /create if applicable)
            void this.router.navigate(["/organization-groups"], {queryParamsHandling: "preserve"})

            if (result?.delete && result.id) {
                await this.notification.withUserFeedback(() => mutateThrowingErrors(this.deleteOrganizationGroupsTableItem)({id: result.id!}), {
                    success: "Group deleted.",
                    error: "Failed to delete group.",
                })
                this.reload$.next()
            } else if (result?.name) {
                if (result.create) {
                    await this.notification.withUserFeedback(
                        () =>
                            mutateThrowingErrors(this.createOrganizationGroupsTableItem)({
                                input: {
                                    name: result.name,
                                    organizationIds: result.organizationIds,
                                },
                            }),
                        {
                            success: "Group created.",
                            error: "Failed to create group.",
                        },
                    )
                    this.reload$.next()
                } else if (result.id) {
                    await this.notification.withUserFeedback(
                        () =>
                            mutateThrowingErrors(this.updateOrganizationGroupsTableItem)({
                                input: {
                                    id: result.id!,
                                    name: result.name,
                                    organizationIds: result.organizationIds,
                                },
                            }),
                        {
                            success: "Group updated.",
                            error: "Failed to update group.",
                        },
                    )
                    this.reload$.next()
                }
            }
        })
    }

    rowClick(item: OrganizationGroupsTableItemFragment | null): void {
        this.showEditDialog(item)
    }

    createItem() {
        this.showEditDialog()
    }

    openCreateDialog() {
        this.showEditDialog()
    }

    organizationNames(organizations?: {name?: string | null}[] | null): string {
        return (organizations ?? []).map((org) => org.name).join(", ")
    }
}
